/** Shared helpers for replacing or closing the single artifact preview. */

import type { TabType } from '@heidi/session/contracts';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { WORK_CHAT_HOME_PATH } from '../../../contracts/projects/chat-home-path';

import {
  clearComposerLinkedItemsAtom,
  closeArtifactAtom,
  setOpenArtifactAtom,
  type Artifact,
} from './state';

// Work chat home, imported rather than restated — one declaration site.
const WORK_HOME_PATH = WORK_CHAT_HOME_PATH;

const isWorkHomePath = () =>
  typeof window !== 'undefined' && window.location.pathname === WORK_HOME_PATH;

export const useArtifactPanelNavigation = () => {
  const closeArtifact = useSetAtom(closeArtifactAtom);
  const clearLinkedItems = useSetAtom(clearComposerLinkedItemsAtom);
  const replaceArtifact = useSetAtom(setOpenArtifactAtom);

  const close = useCallback(() => {
    closeArtifact();
    // Work home links the open artifact into the composer chip. Closing the
    // canvas must clear that chip; project chats keep their own link state.
    // Read pathname from ``window`` so panel unit tests don't need a mounted
    // Next router (``useRouter`` throws when unmounted).
    if (isWorkHomePath()) {
      clearLinkedItems();
    }
  }, [clearLinkedItems, closeArtifact]);

  const openSession = useCallback(
    (sessionId: string, opts?: { focusTab?: TabType; focusDocumentId?: string }) => {
      replaceArtifact({
        type: 'session',
        sessionId,
        focusTab: opts?.focusTab,
        focusDocumentId: opts?.focusDocumentId,
      });
    },
    [replaceArtifact]
  );

  const openPatient = useCallback(
    (patientId: string) => {
      replaceArtifact({ type: 'patient', patientId });
    },
    [replaceArtifact]
  );

  const openDocument = useCallback(
    (sessionId: string, documentId: string) => {
      replaceArtifact({ type: 'document', sessionId, documentId });
    },
    [replaceArtifact]
  );

  const openNote = useCallback(
    (sessionId: string) => {
      replaceArtifact({ type: 'note', sessionId });
    },
    [replaceArtifact]
  );

  const openTasks = useCallback(
    (sessionId: string) => {
      replaceArtifact({ type: 'tasks', sessionId });
    },
    [replaceArtifact]
  );

  const openCodes = useCallback(
    (sessionId: string) => {
      replaceArtifact({ type: 'codes', sessionId });
    },
    [replaceArtifact]
  );

  const openWorkflow = useCallback(
    (workflowId: string) => {
      replaceArtifact({ type: 'workflow', workflowId });
    },
    [replaceArtifact]
  );

  const openMemory = useCallback(
    (memoryId: string) => {
      replaceArtifact({ type: 'memory', memoryId });
    },
    [replaceArtifact]
  );

  const openPresentation = useCallback(
    (presentationId: string) => {
      replaceArtifact({ type: 'presentation', presentationId });
    },
    [replaceArtifact]
  );

  const openTemplateBuilder = useCallback(
    (templateId: string) => {
      replaceArtifact({ type: 'template-builder', templateId });
    },
    [replaceArtifact]
  );

  const openArtifact = useCallback(
    (artifact: Artifact) => {
      replaceArtifact(artifact);
    },
    [replaceArtifact]
  );

  return useMemo(
    () => ({
      close,
      freshOpen: replaceArtifact,
      openSession,
      openPatient,
      openDocument,
      openNote,
      openTasks,
      openCodes,
      openWorkflow,
      openMemory,
      openPresentation,
      openTemplateBuilder,
      openArtifact,
    }),
    [
      close,
      replaceArtifact,
      openSession,
      openPatient,
      openDocument,
      openNote,
      openTasks,
      openCodes,
      openWorkflow,
      openMemory,
      openPresentation,
      openTemplateBuilder,
      openArtifact,
    ]
  );
};
